/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  EntityMapping,
  createMapping,
  createNestedMapping,
  sorted,
} from "helpers";
import { useTenant } from "hooks/settings";
import { Loaded, loadedEndpoint, mapLoaded } from "models/loaded";
import { useMemo } from "react";
import {
  BasketRead,
  ChefGroup,
  ChemicalElement,
  MaterialChemistry,
  MaterialLimit,
  MaterialPhysics,
  MaterialRead,
  ProductGroup,
  SearchContextData,
  SteelGrade as SteelGradeType,
  useGetSearchContextQuery,
} from "src/store/api/generatedApi";

export interface SyncedContextData {
  productGroupsId: number | null;
  steelGradesId: number;
  materialsId: number;
  materialPhysicsId: number;
  fixedMixSetId: number;

  productGroups: SearchProductGroups;
  chemistryGroups: SearchChemistryGroups;
  steelGrades: SearchSteelGrades;

  materials: SearchMaterials;
  materialPhysics: SearchMaterialPhysics;
  materialChemistry: SearchMaterialChemistry;

  materialLimits: MaterialLimit[];

  baskets: SearchBaskets;
  chemicalElements: SearchChemicalElements;

  hasDefaultSets: boolean;
}

export type SyncedContextState = Loaded<SyncedContextData>;

export type ProductGroupId = number & { __productGroupId__: true };
export type SearchProductGroups = EntityMapping<
  ProductGroup & { id: ProductGroupId }
> & { id: number };

export type ChemistryGroupId = number & { __chemistryGroupId__: true };
export type SearchChemistryGroups = EntityMapping<
  ChefGroup & { id: ChemistryGroupId }
>;

export type SteelGradesId = number & { __steelGradesId__: true };
export type SteelGrade = SteelGradeType & { id: SteelGradesId };
export type SearchSteelGrades = EntityMapping<SteelGrade> & { id: number };

export type MaterialsId = number & { __materialsId__: true };
export type SearchMaterials = EntityMapping<
  MaterialRead & {
    id: MaterialsId;
  }
>;

export type MaterialLimitId = number;

export type SearchMaterialPhysics = Omit<
  EntityMapping<MaterialPhysics>,
  "byName"
>;
type ChemistryByElement = Record<number, MaterialChemistry>;
export type SearchMaterialChemistry = Record<number, ChemistryByElement>;

export type SearchBaskets = BasketRead[];
export type SearchChemicalElements = EntityMapping<ChemicalElement>;

export const useSyncedContextState = (
  searchContextId: number | null
): SyncedContextState => {
  const tenant = useTenant();

  const endpoint = useGetSearchContextQuery(
    searchContextId == null
      ? skipToken
      : {
          tenantName: tenant,
          searchContextId,
        }
  );

  const { data, isError, isLoading, isFetching } = endpoint;

  return useMemo(
    () =>
      mapLoaded(
        loadedEndpoint({ data, isError, isLoading, isFetching }),
        transformSearchContext
      ),
    [data, isError, isLoading, isFetching]
  );
};

const transformSearchContext = ({
  has_default_sets,
  product_groups_id,
  steel_grades_id,
  materials_id,
  material_physics_id,
  product_groups,
  chef_groups,
  steel_grades,
  materials,
  material_physics,
  material_chemistry,
  material_limits,
  baskets,
  chemical_elements,
  fixed_mix_set_id,
}: SearchContextData): SyncedContextData => {
  const taggedProductGroups = product_groups.map(({ id, ...productGroup }) => ({
    id: id as ProductGroupId,
    ...productGroup,
  }));

  const taggedChemistryGroups = chef_groups.map(({ id, ...chefGroup }) => ({
    id: id as ChemistryGroupId,
    ...chefGroup,
  }));

  const taggedSteelGrades = steel_grades.map(({ id, ...steel_grade }) => ({
    id: id as SteelGradesId,
    ...steel_grade,
  }));

  const taggedMaterials = materials.map(({ id, ...materials }) => ({
    id: id as MaterialsId,
    ...materials,
  }));

  return {
    hasDefaultSets: has_default_sets,
    productGroupsId: product_groups_id,
    steelGradesId: steel_grades_id,
    materialsId: materials_id,
    materialPhysicsId: material_physics_id,
    fixedMixSetId: fixed_mix_set_id,
    productGroups: {
      byIndex: sorted(taggedProductGroups, (item) => item.name),
      byId: createMapping(taggedProductGroups, (item) => item.id),
      byName: createMapping(taggedProductGroups, (item) => item.name),
      id: product_groups_id!,
    },
    chemistryGroups: {
      byIndex: sorted(taggedChemistryGroups, (item) => item.name),
      byId: createMapping(taggedChemistryGroups, (item) => item.id),
      byName: createMapping(taggedChemistryGroups, (item) => item.name),
    },
    steelGrades: {
      byIndex: sorted(taggedSteelGrades, (item) => item.name),
      byId: createMapping(taggedSteelGrades, (item) => item.id),
      byName: createMapping(taggedSteelGrades, (item) => item.name),
      id: steel_grades_id,
    },
    materials: {
      byIndex: sorted(taggedMaterials, (item) => item.ordering),
      byId: createMapping(taggedMaterials, (material) => material.id),
      byName: createMapping(taggedMaterials, (material) => material.name),
    },
    materialPhysics: {
      byIndex: material_physics,
      byId: createMapping(material_physics, (material) => material.material_id),
    },
    materialChemistry: createNestedMapping(
      material_chemistry,
      (item) => item.material_id,
      (item) => item.chemical_element_id
    ),
    materialLimits: material_limits,
    baskets,
    chemicalElements: {
      byIndex: chemical_elements,
      byId: createMapping(chemical_elements, (item) => item.id),
      byName: createMapping(chemical_elements, (item) => item.name),
    },
  };
};
